import { useState, useEffect, useCallback } from 'react'
import { lumi } from '@/lib/lumi'
import { EVALUATION_ASPECTS } from '@/lib/evaluationAspects'
import { CASE_CATEGORIES_TO_COLLABORATOR, CASE_CATEGORIES_TO_COMPANY } from '@/lib/caseCategories'

const SETTINGS_KEY = 'global'

export interface EvalAspect {
  key: string
  label: string
  description: string
}

export interface CaseCategory {
  value: string
  label: string
}

export interface PlatformSettings {
  _id?: string
  key: string
  platformName: string
  platformDesc: string
  supportEmail: string
  minEvalScore: number
  maxEvalScore: number
  evaluationAspects: EvalAspect[]
  caseCategoriesToCollaborator: CaseCategory[]
  caseCategoriesToCompany: CaseCategory[]
  trainingTypes: string[]
  trainingModalities: string[]
  privacyPolicy: string
  reportsPolicy: string
  evaluationsPolicy: string
  termsAndConditions: string
}

export const DEFAULT_SETTINGS: PlatformSettings = {
  key: SETTINGS_KEY,
  platformName: 'Beeznz Work',
  platformDesc: 'Plataforma integral de reputación laboral, empleabilidad, capacitación y vinculación entre empresas y colaboradores.',
  supportEmail: 'soporte@beeznzwork.com',
  minEvalScore: 1,
  maxEvalScore: 10,
  evaluationAspects: EVALUATION_ASPECTS.map((a) => ({ key: a.key, label: a.label, description: a.description })),
  caseCategoriesToCollaborator: CASE_CATEGORIES_TO_COLLABORATOR.map((c) => ({ value: c.value, label: c.label })),
  caseCategoriesToCompany: CASE_CATEGORIES_TO_COMPANY.map((c) => ({ value: c.value, label: c.label })),
  trainingTypes: ['Curso', 'Taller', 'Seminario', 'Certificación'],
  trainingModalities: ['Presencial', 'En línea', 'Híbrida'],
  privacyPolicy: 'Aviso de Privacidad de Beeznz Work\n\nEn Beeznz Work nos comprometemos a proteger la privacidad de nuestros usuarios. Los datos personales recopilados (nombre, correo electrónico, información profesional) serán utilizados exclusivamente para brindar los servicios de la plataforma.\n\nTus datos no serán vendidos ni compartidos con terceros sin tu consentimiento, salvo obligación legal.\n\nPuedes solicitar la modificación o eliminación de tus datos en cualquier momento escribiendo a soporte@beeznzwork.com.',
  reportsPolicy: 'Reglamento de Reportes y Evidencias de Beeznz Work\n\nLos reportes laborales deben basarse en hechos verídicos y documentados. El usuario que reporte de forma maliciosa o sin sustento podrá ser suspendido de la plataforma.\n\nToda evidencia cargada debe ser legítima. Beeznz Work revisará cada caso antes de integrarlo al historial laboral.\n\nEl proceso de revisión puede tardar hasta 10 días hábiles.',
  evaluationsPolicy: 'Política de Evaluaciones y Reputación Laboral de Beeznz Work\n\nLas evaluaciones deben realizarse de forma honesta y objetiva. Están permitidas una vez por mes por vínculo activo.\n\nLas calificaciones injustificadas o malintencionadas podrán ser removidas previa revisión del equipo de Beeznz Work.\n\nLa reputación de empresas y colaboradores se construye a partir del promedio de evaluaciones recibidas a lo largo del tiempo.',
  termsAndConditions: 'Términos y Condiciones de Beeznz Work\n\nAl crear una cuenta en Beeznz Work aceptas cumplir con las reglas de la plataforma, proporcionar información veraz, y usar los servicios de forma ética y legal.\n\nBeeznz Work se reserva el derecho de suspender cuentas que incumplan las políticas de uso.\n\nEl uso de la plataforma implica la aceptación de estos términos en su versión vigente.',
}

export function usePlatformSettings() {
  const [settings, setSettings] = useState<PlatformSettings>(DEFAULT_SETTINGS)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const res = await lumi.entities.platform_settings.list({
        filter: { key: SETTINGS_KEY },
        limit: 1,
      })
      const doc = res?.list?.[0]
      if (doc) {
        setSettings({ ...DEFAULT_SETTINGS, ...doc } as PlatformSettings)
      } else {
        setSettings(DEFAULT_SETTINGS)
      }
    } catch (error) {
      console.error('[usePlatformSettings] fetch failed:', error)
      setSettings(DEFAULT_SETTINGS)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSettings()
  }, [fetchSettings])

  const persist = useCallback(async (next: PlatformSettings) => {
    setSaving(true)
    const now = new Date().toISOString()
    try {
      // Verificar si ya existe el documento
      const res = await lumi.entities.platform_settings.list({ filter: { key: SETTINGS_KEY }, limit: 1 })
      const existing = res?.list?.[0]
      const payload = {
        key: SETTINGS_KEY,
        platformName: next.platformName,
        platformDesc: next.platformDesc,
        supportEmail: next.supportEmail,
        minEvalScore: Number(next.minEvalScore) || DEFAULT_SETTINGS.minEvalScore,
        maxEvalScore: Number(next.maxEvalScore) || DEFAULT_SETTINGS.maxEvalScore,
        evaluationAspects: next.evaluationAspects,
        caseCategoriesToCollaborator: next.caseCategoriesToCollaborator,
        caseCategoriesToCompany: next.caseCategoriesToCompany,
        trainingTypes: next.trainingTypes,
        trainingModalities: next.trainingModalities,
        privacyPolicy: next.privacyPolicy ?? DEFAULT_SETTINGS.privacyPolicy,
        reportsPolicy: next.reportsPolicy ?? DEFAULT_SETTINGS.reportsPolicy,
        evaluationsPolicy: next.evaluationsPolicy ?? DEFAULT_SETTINGS.evaluationsPolicy,
        termsAndConditions: next.termsAndConditions ?? DEFAULT_SETTINGS.termsAndConditions,
        updatedAt: now,
      }
      if (existing?._id) {
        await lumi.entities.platform_settings.update(existing._id, payload)
        setSettings({ ...payload, _id: existing._id } as PlatformSettings)
      } else {
        const created = await lumi.entities.platform_settings.create({ ...payload, createdAt: now })
        setSettings({ ...payload, _id: created?._id } as PlatformSettings)
      }
      return true
    } catch (error) {
      console.error('[usePlatformSettings] persist failed:', error)
      return false
    } finally {
      setSaving(false)
    }
  }, [])

  return { settings, setSettings, loading, saving, persist, refetch: fetchSettings }
}
